/**
 * Media Upload API Module
 * Handles API communication for media file uploads and deletions.
 */

import { ApiBase } from "../core/api-base";
import type * as z from "zod";
import {
    DeleteMedia200Response,
    DeleteMedia404Response,
    DeleteMedia422Response,
    DeleteMedia500Response,
    DeleteMediaBody,
    UploadMedia201Response,
    UploadMedia422Response,
    UploadMedia500Response,
    UploadMediaBody,
} from "../api/generated/posts.zod";

interface UploadMediaOptions {
    noSpinner?: boolean;
    [key: string]: unknown;
}

type UploadMediaResult = z.infer<typeof UploadMedia201Response>;
type DeleteMediaResult = z.infer<typeof DeleteMedia200Response>;
type UploadMediaErrorResponse = z.infer<typeof UploadMedia422Response> | z.infer<typeof UploadMedia500Response>;
type DeleteMediaErrorResponse =
    | z.infer<typeof DeleteMedia404Response>
    | z.infer<typeof DeleteMedia422Response>
    | z.infer<typeof DeleteMedia500Response>;

/**
 * WysiwygAPI class
 * Provides methods for uploading and deleting media files via API.
 */
class WysiwygAPI extends ApiBase {
    public static async uploadMedia(
        file: File,
        type: z.infer<typeof UploadMediaBody>["type"],
        boardId: z.infer<typeof UploadMediaBody>["board_id"],
        options: UploadMediaOptions = {},
    ): Promise<UploadMediaResult> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("board_id", boardId);
        formData.append("type", type);

        try {
            const requestOptions: UploadMediaOptions = {
                noSpinner: true,
                ...(options || {}),
            };

            const response = await super.apiPost("/api/v1/media/upload", formData, requestOptions);

            if (!response.ok) {
                const errorData = ((await response.json().catch(() => ({}))) as UploadMediaErrorResponse) || {};
                const errorMessage = errorData.message || `Upload failed: HTTP ${response.status}`;
                throw new Error(errorMessage);
            }

            const result = (await response.json()) as UploadMediaResult;
            return result;
        } catch (error) {
            console.error("Media upload error:", error);
            throw error;
        }
    }

    public static async deleteMedia(
        filename: z.infer<typeof DeleteMediaBody>["filename"],
        boardId: z.infer<typeof DeleteMediaBody>["board_id"],
    ): Promise<DeleteMediaResult> {
        try {
            const response = await super.apiRequest(
                "/api/v1/media/delete",
                "DELETE",
                {
                    filename,
                    board_id: boardId,
                },
                { noSpinner: false },
            );

            if (!response.ok) {
                const errorData = ((await response.json().catch(() => ({}))) as DeleteMediaErrorResponse) || {};
                const errorMessage = errorData.message || `Delete failed: HTTP ${response.status}`;
                throw new Error(errorMessage);
            }

            const result = (await response.json()) as DeleteMediaResult;
            return result;
        } catch (error) {
            console.error("Media delete error:", error);
            throw error;
        }
    }
}

export { WysiwygAPI };
